/**
 * Work logs filters (T-40, owner decision D8): the type tabs became a filter,
 * the two date fields one period control. Pure, unit-tested; the page keeps
 * the same API parameters as before.
 */

/** P-37 (D-194 Q-177) — `replies`: only the "מענה לקריאה" logs. */
export type WorkLogType = 'all' | 'tickets' | 'tasks' | 'milestones' | 'replies';
export type PeriodPreset = 'all' | 'today' | 'week' | 'month' | 'lastMonth' | 'custom';

export interface WorkLogFilters {
  type: WorkLogType;
  from: string; // yyyy-MM-dd or ''
  to: string;
  projectId: string;
  /** The task's own engagement (`t.engagement_id`) — how service / hour-bank hours are found (P-02b §6.7). */
  engagementId: string;
  customerId: string;
  timeFrom: string; // HH:mm or ''
  timeTo: string;
  /** The worker (`agent_id`); '' = everyone the role may see. */
  agentId: string;
  /** P-06/E — one task's logs (`ticket_id`); '' = all. */
  ticketId?: string;
  /** P-10d (D-72) — one milestone's logs (`milestone_id`); '' = all. */
  milestoneId?: string;
  /** P-10d (D-72) — the "פתוחות" tab: segments of open work cycles only. */
  openOnly?: boolean;
  /** SEC-2b (D-92, D-104) — "שלי" / "של אחרים". */
  who?: 'mine' | 'others';
  /** SEC-2b (D-104) — the customer (organization) filter. */
  organizationId?: string;
  /**
   * FIX-B9 (D-161, D-168) — the review queue "דורש טיפול עכשיו" opens:
   * `manual` (needs a review, typed by the employee), `auto` (the automatic
   * ones; OTE manager only). '' = no review filter.
   */
  review?: WorkLogReview;
}

export type WorkLogReview = '' | 'pending' | 'manual' | 'auto';

export const EMPTY_WORKLOG_FILTERS: WorkLogFilters = {
  type: 'all',
  from: '',
  to: '',
  projectId: '',
  engagementId: '',
  customerId: '',
  timeFrom: '',
  timeTo: '',
  agentId: '',
};

export const PERIOD_PRESETS: readonly Exclude<PeriodPreset, 'custom'>[] = ['all', 'today', 'week', 'month', 'lastMonth'];

const pad = (n: number) => String(n).padStart(2, '0');
export const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Date range of a preset, in local days. The week starts on Sunday (Israel). */
export function periodRange(preset: Exclude<PeriodPreset, 'custom'>, today: Date): { from: string; to: string } {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (preset) {
    case 'today':
      return { from: isoDay(today), to: isoDay(today) };
    case 'week': {
      const start = new Date(y, m, today.getDate() - today.getDay());
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      return { from: isoDay(start), to: isoDay(end) };
    }
    case 'month':
      return { from: isoDay(new Date(y, m, 1)), to: isoDay(new Date(y, m + 1, 0)) };
    case 'lastMonth':
      return { from: isoDay(new Date(y, m - 1, 1)), to: isoDay(new Date(y, m, 0)) };
    case 'all':
    default:
      return { from: '', to: '' };
  }
}

/** Which preset a range is (so `?month=` and hand-typed ranges show the right label). */
export function presetForRange(from: string, to: string, today: Date): PeriodPreset {
  for (const p of PERIOD_PRESETS) {
    const r = periodRange(p, today);
    if (r.from === from && r.to === to) return p;
  }
  return 'custom';
}

/** `?month=2026-09` → the whole month (the link from Home / reports). */
export function monthParamRange(month: string | null): { from: string; to: string } | null {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return null;
  const [y, m] = month.split('-').map(Number);
  if (m < 1 || m > 12) return null;
  return { from: `${month}-01`, to: `${month}-${pad(new Date(y, m, 0).getDate())}` };
}

/** Filters that live behind the "סינון" trigger (type, project, engagement, customer, task, milestone, time of day). */
export function hiddenFilterCount(f: WorkLogFilters): number {
  return (f.type !== 'all' ? 1 : 0) + (f.projectId ? 1 : 0) + (f.engagementId ? 1 : 0) + (f.customerId ? 1 : 0)
    + (f.ticketId ? 1 : 0) + (f.milestoneId ? 1 : 0) + (f.timeFrom || f.timeTo ? 1 : 0);
}

/**
 * Anything narrowing the list (drives the empty-state copy and "ניקוי הכול").
 * P-10d (D-72): the "ממתין לאישור" / "אושרו" filters were removed.
 */
export function hasAnyFilter(f: WorkLogFilters): boolean {
  return hiddenFilterCount(f) > 0 || !!f.from || !!f.to || !!f.agentId;
}

/** The API parameters, unchanged from the tab era (`task_type` / `milestone_id`). */
export function workLogQueryParams(f: WorkLogFilters, limit = 100) {
  return {
    start_date: f.from || undefined,
    end_date: f.to || undefined,
    customer_id: f.customerId || undefined,
    agent_id: f.agentId || undefined,
    task_type: (f.type === 'tickets' ? 'ticket' : f.type === 'tasks' ? 'task' : undefined) as 'ticket' | 'task' | undefined,
    // "אבני דרך": logs on tasks that belong to a milestone (the log's milestone_id is set);
    // P-10d: one chosen milestone narrows to that milestone.
    milestone_id: f.milestoneId || (f.type === 'milestones' ? '__not_null__' : undefined),
    project_id: f.projectId || undefined,
    engagement_id: f.engagementId || undefined,
    ticket_id: f.ticketId || undefined,
    start_time_from: f.timeFrom || undefined,
    start_time_to: f.timeTo || undefined,
    open_only: f.openOnly ? true : undefined,
    who: f.who,
    organization_id: f.organizationId || undefined,
    review: (f.review || undefined) as Exclude<WorkLogReview, ''> | undefined,
    log_kind: (f.type === 'replies' ? 'reply' : undefined) as 'reply' | undefined,
    limit,
  };
}

/** Share of billable minutes, 0–100, rounded; null when there is nothing to divide. */
export function billableShare(totalMinutes: number, billableMinutes: number): number | null {
  if (!(totalMinutes > 0)) return null;
  return Math.round(Math.min(100, Math.max(0, (billableMinutes / totalMinutes) * 100)));
}
